/**
 * Pure helpers for the consolidated `human` runbook step form. Kept out of the
 * web editor component so the executor, loader, and run-time renderer can all
 * share one set of factories.
 */
import { z } from 'zod/v3';
import type {
  RunbookDecisionDTO,
  RunbookDecisionOptionDTO,
  RunbookHumanFieldOptionDTO,
  RunbookHumanFieldType,
  RunbookHumanFormDTO,
  RunbookHumanFormDisplayDTO,
  RunbookHumanFormFieldDTO,
  RunbookStepDTO,
} from './workflow.dto';
import { slugifyRefId } from '../runbook-refs';

export const HUMAN_FIELD_TYPES: RunbookHumanFieldType[] = [
  'text',
  'files',
  'number',
  'date',
  'url',
  'select',
  'browser_recording',
];

/** Outcome keys of the decision presets. The positive key routes the success
 *  edge, the negative key the failure edge (via each option's `tone`). */
export const DECISION_APPROVE_KEY = 'approve';
export const DECISION_REJECT_KEY = 'reject';
export const DECISION_PASS_KEY = 'pass';
export const DECISION_FAIL_KEY = 'fail';

/** The selectable decision presets. Both are two-outcome positive/negative
 *  forks — only the labels/keys differ — so the run captures the honest outcome
 *  (`approve|reject` vs `pass|fail`) while routing stays tone-based. */
export const APPROVE_REJECT_OPTIONS: RunbookDecisionOptionDTO[] = [
  { key: DECISION_APPROVE_KEY, label: 'Approve', tone: 'positive' },
  { key: DECISION_REJECT_KEY, label: 'Reject', tone: 'negative' },
];
export const PASS_FAIL_OPTIONS: RunbookDecisionOptionDTO[] = [
  { key: DECISION_PASS_KEY, label: 'Pass', tone: 'positive' },
  { key: DECISION_FAIL_KEY, label: 'Fail', tone: 'negative' },
];

/**
 * Canonical human label for a decision outcome key — the value stored in
 * `values[decisionId]` / `completion.decision`. Single source of truth so the
 * evidence readme, artifact log, and UI don't each hardcode approve/reject and
 * silently mislabel a pass/fail (or any future) outcome. Unknown keys pass
 * through unchanged.
 */
export const decisionOutcomeLabel = (
  outcome: string | null | undefined,
): string | null => {
  if (!outcome) return null;
  switch (outcome.toLowerCase()) {
    case DECISION_APPROVE_KEY:
      return 'Approved';
    case DECISION_REJECT_KEY:
      return 'Rejected';
    case DECISION_PASS_KEY:
      return 'Pass';
    case DECISION_FAIL_KEY:
      return 'Fail';
    default:
      return outcome;
  }
};

const randomId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

/** A ref-safe id derived from a label and de-duplicated within its owner. */
export const makeStableHumanFormId = (
  label: string,
  existing: Set<string>,
): string => {
  const base = slugifyRefId(label);
  if (!existing.has(base)) return base;
  let suffix = 2;
  while (existing.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

/** Freeze existing option values and mint deterministic values for new labels. */
export const mintHumanFieldOptionValues = (
  options: Array<{ label: string; value?: string }>,
): RunbookHumanFieldOptionDTO[] => {
  const kept = options.filter((option) => option.label.trim());
  const seen = new Set(
    kept.flatMap((option) => (option.value ? [option.value] : [])),
  );
  return kept.map((option) => {
    if (option.value) return { label: option.label, value: option.value };
    const value = makeStableHumanFormId(option.label, seen);
    seen.add(value);
    return { value, label: option.label };
  });
};

export const createHumanFormField = (
  overrides: Partial<RunbookHumanFormFieldDTO> = {},
): RunbookHumanFormFieldDTO => {
  const type = overrides.type ?? 'text';
  return {
    id: overrides.id || randomId('hfield'),
    label: overrides.label || '',
    type,
    required: overrides.required ?? false,
    help: overrides.help,
    // Signing is text-only.
    sign: type === 'text' ? overrides.sign : undefined,
    // Options are `select`-only.
    options: type === 'select' ? (overrides.options ?? []) : undefined,
  };
};

export const createHumanFormDisplay = (
  overrides: Partial<RunbookHumanFormDisplayDTO> = {},
): RunbookHumanFormDisplayDTO => ({
  id: overrides.id || randomId('hdisp'),
  label: overrides.label || '',
  value: overrides.value || '',
});

/**
 * The Approve/Reject decision preset a step's form gets when its decision gate
 * is switched on. `approve` routes the success edge, `reject` the failure edge.
 * Today exactly two options; the shape supports more (see RunbookDecisionDTO).
 */
export const createDefaultDecision = (
  overrides: Partial<RunbookDecisionDTO> = {},
): RunbookDecisionDTO => ({
  // A form has at most one decision, so a fixed step-local id is unique within
  // the step (the ref namespaces by step) and ref-safe — no random suffix.
  id: overrides.id || 'decision',
  label: overrides.label || 'Decision',
  help: overrides.help,
  reasonRequired: overrides.reasonRequired ?? false,
  // Copy the preset so the exported constant is never shared into mutable state.
  options: overrides.options ?? APPROVE_REJECT_OPTIONS.map((o) => ({ ...o })),
});

/**
 * The form a freshly-added `human` step starts with: a required Notes field
 * (always record what happened) and an optional Files field (evidence is
 * usually files, so the field is right there ready).
 */
export const createDefaultHumanForm = (): RunbookHumanFormDTO => ({
  displayData: [],
  fields: [
    // Ref-safe, step-local ids so the outputs read as `@step.<id>.notes` /
    // `.files` rather than a random `@hfield-…`.
    createHumanFormField({ id: 'notes', label: 'Notes', type: 'text', required: true }),
    createHumanFormField({ id: 'files', label: 'Files', type: 'files', required: false }),
  ],
});

/** Is this the human-gated step kind? */
export const isHumanStepKind = (kind: RunbookStepDTO['kind']): boolean =>
  kind === 'human';

/** Run-output key holding the reason captured alongside a decision. */
export const decisionReasonOutputKey = (decisionId: string): string =>
  `${decisionId}_reason`;

const FIELD_OUTPUT_TYPE: Record<RunbookHumanFieldType, string> = {
  text: 'string',
  files: 'file[]',
  number: 'string',
  date: 'string',
  url: 'string',
  // The chosen option's value — a string. When the field carries options,
  // humanStepOutputs narrows this to the `a | b | c` union (below).
  select: 'string',
  // A browser recording the operator captures for this field; the value is the
  // recording id(s), and the screenshots + signed manifest become step evidence.
  browser_recording: 'recording[]',
};

/**
 * Implicit run outputs a `human` step produces — one per field (keyed by field
 * id, stable unlike labels) plus, when the form has a decision gate, the chosen
 * outcome (keyed by `decision.id`) and its reason. Mirrors
 * RUNBOOK_HUMAN_STEP_OUTPUTS for the legacy kinds.
 */
export function humanStepOutputs(
  form: RunbookHumanFormDTO,
): Array<{ name: string; type: string }> {
  const outputs = form.fields.map((field) => ({
    name: field.id,
    // A `select` narrows to the union of its option values, mirroring how a
    // decision exposes `approve | reject` — so downstream refs see the choices.
    type:
      field.type === 'select' && field.options?.length
        ? field.options.map((option) => option.value).join(' | ')
        : FIELD_OUTPUT_TYPE[field.type],
  }));
  if (form.decision) {
    outputs.push({
      name: form.decision.id,
      type: form.decision.options.map((option) => option.key).join(' | '),
    });
    outputs.push({
      name: decisionReasonOutputKey(form.decision.id),
      type: 'string',
    });
  }
  return outputs;
}

// ========== Zod schemas (runtime validation) ==========
// Mirror the RunbookHumanForm* interfaces in workflow.dto.ts. Used to validate
// a model- or client-authored `human` form at the API boundary (the structured
// `steps` channel of saveDefinition / createRunbook is otherwise z.unknown()).

export const RunbookHumanFieldOptionSchema = z.object({
  // Non-empty: the captured value AND the run-time <Select> item value — Radix
  // throws on an empty-string option value, so reject it at the boundary.
  value: z.string().min(1),
  label: z.string(),
});

export const RunbookHumanFieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum([
    'text',
    'files',
    'number',
    'date',
    'url',
    'select',
    'browser_recording',
  ]),
  required: z.boolean(),
  help: z.string().optional(),
  // `sign` is meaningful on `text` only; the factory nulls it elsewhere.
  sign: z.boolean().optional(),
  // `options` is meaningful on `select` only; the factory nulls it elsewhere.
  options: z.array(RunbookHumanFieldOptionSchema).optional(),
});

export const RunbookHumanFormDisplaySchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
    const display = value as Record<string, unknown>;
    if (display.id !== undefined) return value;
    if (typeof display.label !== 'string' || typeof display.value !== 'string') {
      return value;
    }
    // Legacy procedure exports predate display ids. Use the canonical factory
    // so re-imported forms receive the same valid shape as newly-authored ones.
    return createHumanFormDisplay({
      label: display.label,
      value: display.value,
    });
  },
  z.object({
    id: z.string(),
    label: z.string(),
    value: z.string(),
  }),
);

export const RunbookDecisionOptionSchema = z.object({
  key: z.string(),
  label: z.string(),
  tone: z.enum(['positive', 'negative']).optional(),
});

export const RunbookDecisionSchema = z.object({
  id: z.string(),
  label: z.string(),
  help: z.string().optional(),
  reasonRequired: z.boolean().optional(),
  options: z.array(RunbookDecisionOptionSchema).min(1),
});

export const RunbookHumanFormSchema = z.object({
  displayData: z.array(RunbookHumanFormDisplaySchema),
  fields: z.array(RunbookHumanFieldSchema),
  decision: RunbookDecisionSchema.optional(),
});

/**
 * Boundary schema for a persisted runbook step on the structured `steps`
 * channel: validate the `human` form when present, pass every other step field
 * through untouched (the channel persists `RunbookStepDTO` verbatim). Catches a
 * malformed/legacy form (e.g. a removed `decision` field type) without
 * reshaping the rest of the step.
 */
export const RunbookStepHumanFormBoundarySchema = z
  .object({ human: RunbookHumanFormSchema.optional() })
  .passthrough();
